import React, { FC, useCallback, useEffect, useState } from 'react';

import Xmark from '@gravity-ui/icons/Xmark';
import { Button, Icon, Loader, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { aliceAgentAppShareApi } from 'client/api';
import { ApiError } from 'client/api/error';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as keyset from 'i18n/AliceAgentAppAccess';

import { Field } from '../form/Field/Field';

import styles from './AliceAgentAppSharingPage.module.scss';

const t = i18n(keyset);

type AgentAppShareUser = aliceAgentAppShareApi.AgentAppShareUser;

interface Props {
    app: AliceAgentAppDTO;
}

export const AliceAgentAppUsersListSection: FC<Props> = ({ app }) => {
    const [users, setUsers] = useState<AgentAppShareUser[]>([]);
    const [loginInput, setLoginInput] = useState('');
    const [loginError, setLoginError] = useState<string | undefined>();

    // Memoized so that useFetchingHandler receives a stable reference — prevents
    // fetchUsersHandler / fetchUsers from changing identity on every render.
    const getDraftSharesHandler = useCallback(() => aliceAgentAppShareApi.getDraftShares(app.skillId), [app.skillId]);

    const [fetchUsersHandler, fetchState] = useFetchingHandler({
        handler: getDraftSharesHandler,
    });

    const fetchUsers = useCallback(async () => {
        const result = await fetchUsersHandler();
        if (result) setUsers(result);
    }, [fetchUsersHandler]);

    useEffect(() => {
        void fetchUsers();
    }, [fetchUsers]); // stable: changes only when skillId changes

    const [addShareHandler, addState] = useFetchingHandler({
        handler: (login: string) => aliceAgentAppShareApi.addDraftShare(app.skillId, login),
        throwErrors: true,
    });

    const handleAdd = useCallback(async () => {
        const trimmed = loginInput.trim();
        if (!trimmed) return;
        setLoginError(undefined);
        try {
            const newUser = await addShareHandler(trimmed);
            if (newUser) {
                // POST идемпотентен: если пользователь уже есть — не дублировать в списке
                setUsers((prev) => (prev.some((u) => u.uid === newUser.uid) ? prev : [...prev, newUser]));
                setLoginInput('');
            }
        } catch (err) {
            if (err instanceof ApiError && err.payload?.typedErrors?.length) {
                const { type } = err.payload.typedErrors[0];
                setLoginError(
                    type === 'LOGIN_NOT_FOUND' ? t('draft-shares-error-not-found') : t('draft-shares-error-validation'),
                );
            }
        }
    }, [loginInput, addShareHandler]);

    const handleDelete = useCallback(
        async (userId: string) => {
            // DELETE идемпотентен: если шеринга нет — всё равно 200
            await aliceAgentAppShareApi.deleteDraftShare(app.skillId, userId);
            setUsers((prev) => prev.filter((u) => u.uid !== userId));
        },
        [app.skillId],
    );

    return (
        <div className={styles.section}>
            <Field
                contentClassName={styles.field}
                fieldTitle={t('draft-shares-header')}
                fieldDescription={t('draft-shares-description')}
                error={loginError}
            >
                <TextInput
                    className={styles.input}
                    value={loginInput}
                    onUpdate={(val) => {
                        setLoginInput(val);
                        setLoginError(undefined);
                    }}
                    placeholder={t('draft-shares-add-placeholder')}
                    size="xl"
                    validationState={loginError ? 'invalid' : undefined}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') void handleAdd();
                    }}
                />
                <Button
                    view="action"
                    size="l"
                    onClick={handleAdd}
                    loading={addState === 'loading'}
                    disabled={!loginInput.trim()}
                >
                    {t('draft-shares-add-button')}
                </Button>
            </Field>

            <p className={styles.usersListDescription}>{t('draft-shares-user-list')}</p>

            {fetchState === 'loading' ? (
                <div className={styles.loader}>
                    <Loader size="s" />
                </div>
            ) : (
                <ul className={styles.usersList}>
                    {users.length === 0 ? (
                        <li className={styles.emptyState}>{t('draft-shares-empty')}</li>
                    ) : (
                        users.map((user) => (
                            <li key={user.uid} className={styles.user}>
                                <span>{user.login}</span>
                                <Button
                                    view="flat"
                                    title={t('error-deny-access')}
                                    onClick={() => void handleDelete(user.uid)}
                                >
                                    <Icon data={Xmark} />
                                </Button>
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};
